import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitText from "../utils/splitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export default class GradientText {
    constructor(query) {
        const el = document.querySelector(query);

        if (!el) return;

        this.el = el;
        this.title = this.el.querySelector('.gradient-text__title');
        this.inner = this.el.querySelector('.gradient-text__inner');

        if (!this.title || !this.inner) return;

        this.mm = gsap.matchMedia();
        this.split = null;
        this.timeline = null;
        this.gradientPhrases = [];

        this.sectionHeight = 0;
        this.setSectionHeight();

        this.init();
    }

    setSectionHeight() {
        const screenHeight = window.screen && window.screen.availHeight ? window.screen.availHeight : 0;
        const innerHeight = window.innerHeight || 0;
        const viewportHeight = window.visualViewport && window.visualViewport.height
            ? window.visualViewport.height
            : 0;

        const height = Math.max(screenHeight, innerHeight, viewportHeight);

        this.sectionHeight = height;
        this.el.style.setProperty('--gradient-pin-height', `${height}px`);
    }

    init() {
        this.mm.add("(max-width: 1024px)", () => {
            this.initMobile();
        });

        this.mm.add("(min-width: 1025px)", () => {
            this.initDesktop();
        });
    }

    splitTitle() {
        if (this.split) {
            this.split.revert();
        }

        this.gradientPhrases = [];

        const GRAY = '#303C46';

        this.split = new SplitText(this.title, {
            type: 'lines, words',
            linesClass: 'gt-line',
            wordsClass: 'gt-word',
        });

        this.lines = this.split.lines;
        this.words = this.split.words;

        this.words.forEach((word) => {
            word.style.color = GRAY;
            word.style.background = 'none';
            word.style.webkitTextFillColor = '';
        });

        this.collectGradientPhrases(GRAY);
    }

    collectGradientPhrases(gray) {
        const phraseNodes = [...this.title.querySelectorAll('.gradient strong, strong')];

        phraseNodes.forEach((phrase) => {
            phrase.classList.add('gradient-phrase--init');
            const wordsInside = this.words.filter((word) => phrase.contains(word));

            if (!wordsInside.length) return;

            const startIndex = this.words.indexOf(wordsInside[0]);
            const phraseText = wordsInside
                .map((word) => word.textContent.trim())
                .join(' ');

            wordsInside.forEach((word) => {
                word.classList.add('is-in-gradient-phrase');
            });

            phrase.innerHTML = phraseText;
            phrase.classList.add('gradient-phrase', 'is-animated');

            gsap.set(phrase, {
                '--gt-c1': gray,
                '--gt-c2': gray,
                '--gt-c3': gray,
                '--gt-c4': gray,
            });

            phrase.style.setProperty('--gt-c1', gray);
            phrase.style.setProperty('--gt-c2', gray);
            phrase.style.setProperty('--gt-c3', gray);
            phrase.style.setProperty('--gt-c4', gray);

            this.gradientPhrases.push({
                el: phrase,
                startIndex,
            });
        });
    }

    initMobile() {
        this.splitTitle();

        this.createTimeline({
            start: 'center center',
            end: `+=${this.sectionHeight}`,
            pin: true,
        });
    }

    initDesktop() {
        this.splitTitle();

        this.createTimeline({
            start: 'center center',
            end: `+=${this.sectionHeight}`,
            pin: true,
            markers: true,
        });
    }

    createTimeline({ start, end, pin = false }) {
        if (this.timeline) {
            this.timeline.kill();
        }

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: this.el,
                start,
                end,
                pin,
                scrub: true,
                anticipatePin: 1,
                invalidateOnRefresh: false,
            }
        });

        this.words.forEach((word, index) => {
            if (word.classList.contains('is-in-gradient-phrase')) return;

            tl.to(word, {
                color: '#EBEFF2',
                ease: 'none',
                duration: 1,
            }, index * 0.14);
        });

        this.gradientPhrases.forEach((phrase) => {
            const pos = phrase.startIndex * 0.14;

            tl.fromTo(phrase.el, {
                '--gt-c1': '#303C46',
                '--gt-c2': '#303C46',
                '--gt-c3': '#303C46',
                '--gt-c4': '#303C46',
            }, {
                '--gt-c1': '#EBEFF2',
                '--gt-c2': '#FDA9CC',
                '--gt-c3': '#FF90BF',
                '--gt-c4': '#FF2584',
                ease: 'none',
                duration: 1,
                immediateRender: true,
            }, pos);
        });

        this.timeline = tl;
    }
}